import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { PageHero } from "@/pages/ServicesPage";
import { EMAIL, IMAGES, PHONE_DISPLAY, PHONE_TEL, SERVICES } from "@/data/content";

const AREA_SECTIONS = [
  {
    name: "Calgary",
    image: IMAGES.modernLawnHome,
    alt: "Modern Calgary home with a new patio and manicured lawn",
    heading: "Luxury Landscaping in Calgary",
    body: [
      "From architectural new builds in Aspen Woods to established estate homes across West Calgary, we design and build luxury backyards that feel inevitable — as though the house and the land were always meant to be together. Our Calgary work includes complete landscape design and construction, Kootenay brownstone retaining walls, garden beds, outdoor kitchens, and the mature spruce, pine and aspen that make a new property feel settled from its first summer.",
      "As a Calgary landscaping studio focused on high-end residential work, we keep every project under one roof: one design, one build team, one accountable point of contact from first sketch to final planting.",
    ],
  },
  {
    name: "Springbank",
    image: IMAGES.fenceRoad,
    alt: "Acreage entrance road lined with trees and ranch fencing near Springbank",
    heading: "Springbank Landscaping for Acreage Properties",
    body: [
      "Springbank acreages ask more of a landscape — long driveways and entrances, shelter from foothills wind, grading and drainage across real distances, and outdoor rooms positioned to hold a mountain view. Springbank landscaping is our home ground: we plan and build complete acreage transformations, from entrance features and brownstone retaining work to fire features and layered shelterbelt planting of pine, larch and aspen.",
      "Every Springbank project begins with a walk of your land and one master plan, so a multi-phase build still feels like a single, coherent estate.",
    ],
  },
  {
    name: "Bearspaw",
    image: IMAGES.wildflowerPath,
    alt: "Natural stone pathway through a wildflower garden on a Bearspaw estate",
    heading: "Bearspaw Estate Landscaping",
    body: [
      "Bearspaw's estate properties deserve landscapes with a sense of permanence. We design and build for Bearspaw homeowners who want the whole environment considered — natural stone terraces, garden beds that bloom in sequence, cedar hot tubs and saunas, evening lighting, irrigation and mature trees set precisely where they will matter in ten years.",
      "Whether your property is an established estate or a new build waiting for its grounds, our team handles the complex work other landscapers avoid: difficult grades, drainage, and phased construction around a lived-in home.",
    ],
  },
  {
    name: "Bragg Creek",
    image: IMAGES.coveredPatio,
    alt: "Covered timber patio with outdoor dining on a wooded Bragg Creek property",
    heading: "Bragg Creek Foothills Landscaping",
    body: [
      "Landscaping in Bragg Creek means working with the foothills, not against them — natural stone that belongs to the site, native and hardy planting, fire-wise design, and outdoor spaces that make the most of a wooded, sloped property.",
      "We build Bragg Creek landscapes in careful phases: grading and drainage first, then stone and structure, then the aspen, spruce and garden beds that settle the whole composition into the forest around it.",
    ],
  },
];

const FAQS = [
  {
    q: "How much does luxury landscaping cost in Calgary?",
    a: "Most of our Calgary, Springbank and Bearspaw projects begin around $75,000 for a focused transformation — a principal stone terrace, planting, lighting and irrigation. Complete outdoor-living environments typically range from $150,000 to $350,000, and full estate developments with pools, entrances and grade work run $350,000 and beyond. Every quote begins with a walk of your property and an honest written conversation about scope.",
  },
  {
    q: "Do you work on acreages in Springbank and Bearspaw?",
    a: "Yes — acreage and estate properties are the heart of our work. We regularly design and build across Springbank, Bearspaw and Rocky View County, including long driveways and entrances, grading and drainage, retaining walls, and complete outdoor-living environments phased over one or more seasons.",
  },
  {
    q: "When is the best time to start a landscaping project in Alberta?",
    a: "Winter is design season, and spring is when the best construction calendars fill. Homeowners who begin the conversation between November and March typically break ground first in spring. That said, we plan and build from April through October, and autumn is the finest planting window of the year.",
  },
  {
    q: "Can you handle difficult grading and drainage?",
    a: "Yes. Grade and water decide the true success of an acreage landscape, and solving them is everyday work for our team — retaining walls, terracing, drainage and site preparation are usually the first phase of a Springbank, Bearspaw or Bragg Creek build.",
  },
  {
    q: "Can a large project be built in phases?",
    a: "Absolutely — most estate projects are. We create one master plan first, then phase construction sensibly: the bones (grading, drainage, driveways, retaining), then the outdoor rooms (patios, kitchens, pools, fire features), then the softness (planting, lighting, irrigation). One plan keeps every phase coherent.",
  },
  {
    q: "How do I request a landscaping quote?",
    a: "Tell us about your property through our private quote form — it takes a few minutes, and you can attach photos or plans. We personally review every request and respond within two business days. Prefer to talk? Call us at (587) 969-0569.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function LuxuryLandscapingPage() {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Landscaper",
    name: "Estate Outdoors Calgary",
    description: "Luxury landscape design and construction for estate homes, new builds and acreage properties in Calgary, Springbank, Bearspaw, Bragg Creek and Rocky View County.",
    url: window.location.origin,
    telephone: "+1-587-969-0569",
    email: EMAIL,
    areaServed: ["Calgary", "Springbank", "Bearspaw", "Bragg Creek", "Elbow Valley", "Church Ranches", "Watermark", "Aspen Woods", "Cochrane", "Rocky View County", "West Calgary"],
    address: { "@type": "PostalAddress", addressLocality: "Calgary", addressRegion: "AB", addressCountry: "CA" },
  };

  return (
    <div data-testid="seo-landing-page">
      <SEO
        title="Luxury Landscaping Calgary | Springbank, Bearspaw & Bragg Creek"
        description="Estate Outdoors designs and builds luxury landscapes in Calgary, Springbank, Bearspaw and Bragg Creek. Acreage, estate and new-build landscaping by one dedicated team. Request a private quote."
        jsonLd={[localBusinessSchema, FAQ_SCHEMA]}
      />

      <PageHero
        testid="seo-hero"
        eyebrow="Calgary · Springbank · Bearspaw · Bragg Creek"
        title="Luxury Landscaping for Calgary's Finest Properties"
        subtitle="One design-build studio for estate homes, acreages and luxury new builds — from first sketch to final planting."
        image={IMAGES.gardenWall}
      />

      {/* Intro */}
      <section className="bg-[#F2EDE4] py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight leading-[1.15]">
              Acreage, Estate & New-Build Landscaping, Designed and Built by One Team
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#4A524C]">
              Estate Outdoors Calgary is a luxury landscape design-build studio serving Calgary, Springbank,
              Bearspaw, Bragg Creek and Rocky View County. We create complete outdoor-living environments —
              natural stone, outdoor kitchens, pools, fire features, planting and lighting — for homeowners who
              want their property handled with care, craft and a single accountable team.
            </p>
          </Reveal>
          <Reveal delay={0.18} className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/quote" data-testid="seo-intro-quote-btn" className="btn-primary">
              Request a Private Quote <ArrowRight size={16} />
            </Link>
            <a href={PHONE_TEL} data-testid="seo-intro-call-btn" className="btn-outline-dark">
              <Phone size={15} /> {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Area sections */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {AREA_SECTIONS.map((area, i) => (
            <div
              key={area.name}
              data-testid={`seo-area-${area.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`grid items-center gap-10 lg:gap-16 border-b border-[#E0D8CC] py-16 lg:grid-cols-12 ${i === 0 ? "pt-0" : ""}`}
            >
              <Reveal className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative">
                  <div className="absolute -inset-3 border border-[#C5A059]/25" aria-hidden="true" />
                  <img src={area.image} alt={area.alt} loading="lazy" className="relative h-[320px] sm:h-[400px] w-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={0.1} className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="eyebrow flex items-center gap-2"><MapPin size={13} aria-hidden="true" /> {area.name}</p>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light tracking-tight leading-[1.15]">{area.heading}</h2>
                {area.body.map((p, j) => (
                  <p key={j} className="mt-5 text-base leading-relaxed text-[#4A524C]">{p}</p>
                ))}
                <Link to="/quote" data-testid={`seo-area-quote-link-${area.name.toLowerCase().replace(/\s+/g, "-")}`} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#8B5A2B] hover:text-[#C5A059] transition-colors">
                  Request a {area.name} Quote <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* Services strip */}
      <section className="bg-[#F2EDE4] py-20 sm:py-24" data-testid="seo-services-strip">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Estate Landscaping Services</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-[#4A524C]">
              Every element of the outdoor environment, designed and built under one roof.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-px bg-[#E0D8CC] border border-[#E0D8CC] sm:grid-cols-2 lg:grid-cols-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={0.03 * i}>
                <Link to="/services" data-testid={`seo-service-link-${i + 1}`} className="group flex h-full items-center justify-between gap-3 bg-[#FAF7F2] px-5 py-5 transition-colors duration-300 hover:bg-white">
                  <span className="font-display text-lg leading-snug group-hover:text-[#8B5A2B] transition-colors">{s.title}</span>
                  <ArrowUpRight size={15} className="shrink-0 text-[#C5A059]" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28" data-testid="seo-faq-section">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="eyebrow !text-[#8B5A2B]">Questions, Answered Honestly</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-light tracking-tight">Frequently Asked Questions</h2>
          </Reveal>
          <div className="mt-10 divide-y divide-[#E0D8CC] border-y border-[#E0D8CC]">
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`seo-faq-${i + 1}`} className="group py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                  <h3 className="font-display text-xl font-normal">{f.q}</h3>
                  <span className="font-display text-2xl text-[#C5A059] transition-transform duration-300 group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-4 text-base leading-relaxed text-[#4A524C]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative grain bg-[#1C201D] py-20 sm:py-28 text-[#FAF7F2]" data-testid="seo-final-cta">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <p className="eyebrow !text-[#C5A059]">Calgary · Springbank · Bearspaw · Bragg Creek</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15]">
              Tell Us About Your Dream Backyard.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#B9BDB4]">
              Share your property, your vision and your investment range. We review every request personally
              and respond within two business days.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/quote" data-testid="seo-final-quote-btn" className="btn-primary">
              Request a Private Quote <ArrowRight size={16} />
            </Link>
            <a href={PHONE_TEL} data-testid="seo-final-call-btn" className="btn-outline-light">
              <Phone size={15} /> Call {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
